import axios from "axios"
import { User } from "../models/user"
import { Service } from "../services/Service"
import StoreService from "../services/store"

const serverBaseURL = process.env.REACT_APP_API_BASE_URL
class UserApi {
    private static store = StoreService.getInstance()

    /**
     * Crée un utilisateur
     * @param user partie de l'user minimum pour créer un compte
     */
    public static async createUser(
        user: Partial<User>,
        sendEmail = true,
        lang = "fr",
        createGroup = false
    ): Promise<
        | { message: string; group?: string; id: string; code?: number }
        | undefined
    > {
        const headers = await Service.getRequestHeaders()
        const response = await axios.post(
            `${serverBaseURL}/user?send_email=${sendEmail}&lang=${lang}&create_group=${createGroup}`,
            user,
            { headers }
        )
        if (response.status === 200) {
            return response.data
        } else if (response.status === 202) {
            return { ...response.data, code: 202 }
        }
        return undefined
    }

    /**
     * Crée un session pour l'utilisateur
     * @param user
     */
    public static async login(user: any): Promise<{
        err: boolean
        data: { accessToken: string; refreshToken: string } | undefined
        statusCode: number
    }> {
        const response = await axios.post(`${serverBaseURL}/auth`, {
            login: user.login,
            password: user.password,
        })

        return {
            err: response.status !== 201,
            data: response.data,
            statusCode: response.status,
        }
    }

    /**
     * Refresh de la session de l'utilisateur
     * @param refreshToken
     */
    public static async refreshSession(
        refreshToken: string
    ): Promise<{ accessToken: string; refreshToken: string }> {
        const headers = await Service.getRequestHeaders()

        const response = await axios.post(
            `${serverBaseURL}/auth/refresh-token`,
            { refreshToken },
            { headers }
        )

        return response.data
    }
    /**
     * Crée un session pour l'utilisateur
     * @param user
     */
    public static async loginAs(
        user: any,
        userId: string
    ): Promise<{
        err: boolean
        data: { accessToken: string; refreshToken: string } | undefined
        statusCode: number
    }> {
        const headers = await Service.getRequestHeaders()

        const response = await axios.post(
            `${serverBaseURL}/auth/as/${userId}`,
            {
                login: user.login,
                password: user.password,
            },
            { headers }
        )

        return {
            err: response.status !== 201,
            data: response.data,
            statusCode: response.status,
        }
    }

    public static async getByGroup(groupId: string, pl?: number) {
        const headers = await Service.getRequestHeaders()
        // construction de la query param
        let queryParams = Number.isInteger(pl) ? `?pl=${pl}` : ""

        const response = await axios.get(
            `${serverBaseURL}/users/groups/${groupId}${queryParams}`,
            {
                headers,
            }
        )
        if (response.status === 200) {
            return response.data
        }
        return undefined
    }

    public static async getUsers(
        groupId: string,
        includeOwner?: boolean,
        limit?: number,
        page?: number,
        orderBy?: string,
        sort?: string,
        q?: string,
        withTotal?: boolean
    ): Promise<any> {
        const headers = await Service.getRequestHeaders()
        let queryParams = ""
        // construction de la query param
        // si moteur de recherche ingnore les filtres
        if (q && q !== "null") {
            queryParams = "?q=" + q
            queryParams += `&group=${groupId}&include_owner=${includeOwner}`
        } else {
            queryParams = `?group=${groupId}&include_owner=${includeOwner}${
                Number.isInteger(limit) ? `&limit=${limit}` : ""
            }`

            if (Number.isInteger(page))
                queryParams +=
                    queryParams.indexOf("?") === -1
                        ? `?page=${page}`
                        : `&page=${page}`
            if (orderBy && sort)
                queryParams +=
                    queryParams.indexOf("?") === -1
                        ? `?orderBy=${orderBy}&sort=${sort}`
                        : `&orderBy=${orderBy}&sort=${sort}`
            if (withTotal)
                queryParams +=
                    queryParams.indexOf("?") === -1
                        ? `?with_total=true`
                        : `&with_total=true`
        }

        const response = await axios.get(
            `${serverBaseURL}/users${queryParams}`,
            {
                headers,
            }
        )
        if (response.status === 200) {
            return response.data
        }
        return undefined
    }

    public static async getUserDetails(
        userId: string,
        link_auth?: string
    ): Promise<any> {
        const headers = await Service.getRequestHeaders()
        // construction de la query param
        const queryParams = link_auth ? `?link_auth=${link_auth}` : ""
        const response = await axios.get(
            `${serverBaseURL}/users/${userId}${queryParams}`,
            {
                headers,
            }
        )
        if (response.status === 200) {
            return response.data
        }
        return undefined
    }

    public static async getUserStats(group?: string): Promise<any> {
        const headers = await Service.getRequestHeaders()
        // construction de la query param
        const queryParams = group ? `?group=${group}` : ""
        const response = await axios.get(
            `${serverBaseURL}/users/stats/count/users${queryParams}`,
            {
                headers,
            }
        )
        if (response.status === 200) {
            return response.data
        }
        return undefined
    }

    public static async getAdminStats(): Promise<any> {
        const headers = await Service.getRequestHeaders()
        const response = await axios.get(
            `${serverBaseURL}/users/stats/count/admins`,
            {
                headers,
            }
        )
        if (response.status === 200) {
            return response.data
        }
        return undefined
    }

    public static async updateUser(userId: string, user: any): Promise<any> {
        const headers = await Service.getRequestHeaders()

        const response = await axios.patch(
            `${serverBaseURL}/user/${userId}`,

            user,

            {
                headers,
            }
        )
        if (response.status === 200) {
            return response.data
        }
        return undefined
    }

    public static async activateAccount(link: string): Promise<any> {
        const headers = await Service.getRequestHeaders()
        const response = await axios.patch(
            `${serverBaseURL}/user?link_auth=${link}`,

            {
                headers,
            }
        )
        if (response.status === 200) {
            return response.data
        }
        return undefined
    }

    public static async deleteUser(userId: string): Promise<any> {
        const headers = await Service.getRequestHeaders()
        const response = await axios.delete(
            `${serverBaseURL}/users/${userId}`,

            {
                headers,
            }
        )
        if (response.status === 200) {
            return response.data
        }
        return undefined
    }

    public static async deleteAuthLink(link: string): Promise<any> {
        const headers = await Service.getRequestHeaders()
        const response = await axios.delete(
            `${serverBaseURL}/link/${link}`,

            {
                headers,
            }
        )
        if (response.status === 200) {
            return response.data
        }
        return undefined
    }

    public static async changePassword(
        passwords: {
            oldPassword?: string
            password: string
            passwordConfirmation: string
        },
        link: string,
        lang: string = "fr"
    ): Promise<any> {
        const headers = await Service.getRequestHeaders()

        const response = await axios.patch(
            `${serverBaseURL}/users/auth?link_auth=${link}&lang=${lang}`,

            passwords,

            {
                headers,
            }
        )
        if (response.status === 200) {
            return response.data
        }
        return undefined
    }

    public static async sendChangePassword(
        to: string,
        from: string,
        lang: string = "fr"
    ): Promise<any> {
        const headers = await Service.getRequestHeaders()

        const response = await axios.post(
            `${serverBaseURL}/users/${to}/send/pwd/change?lang=${lang}`,

            { from },

            {
                headers,
            }
        )
        if (response.status === 200) {
            return response.data
        }
        return undefined
    }

    public static async sendInvitation(
        to: string,
        from: string,
        lang: string = "fr"
    ): Promise<any> {
        const headers = await Service.getRequestHeaders()

        const response = await axios.post(
            `${serverBaseURL}/users/${to}/send/welcome?lang=${lang}`,
            { from },
            {
                headers,
            }
        )
        if (response.status === 200) {
            return response.data
        }
        return undefined
    }

    public static async sendForgottenPwd(
        to: string,
        lang: string = "fr"
    ): Promise<any> {
        const headers = await Service.getRequestHeaders()

        const response = await axios.post(
            `${serverBaseURL}/users/send/forgotten?lang=${lang}`,
            { email: to },
            {
                headers,
            }
        )
        if (response.status === 200) {
            return response.data
        }
        return undefined
    }

    /**
     * update user preferences
     */
    public static async updatePreferences(
        userId: string,
        preferences: any,
        type: string
    ): Promise<any> {
        const headers = await Service.getRequestHeaders()

        const response = await axios.patch(
            `${serverBaseURL}/users/${userId}/pref?type=${type}`,

            preferences,

            {
                headers,
            }
        )
        if (response.status === 200) {
            return response.data
        }
        return undefined
    }
    /**
     * get user Preferences
     * @param userId
     * @param type key correspondantau preferences qu'on veut récupérer
     */
    public static async getPreferences(
        userId: string,
        type: string
    ): Promise<any> {
        const headers = await Service.getRequestHeaders()

        const response = await axios.get(
            `${serverBaseURL}/users/${userId}/pref?type=${type}`,
            {
                headers,
            }
        )
        if (response.status === 200) {
            return response.data
        }
        return undefined
    }
    /**
     * checkHasEntity
     * @param login login user webId/email
     * @param password mot de passe user
     */
    public static async checkHasEntity(
        login: string,
        password: string
    ): Promise<
        { supervisors: Array<string>; users: Array<string> } | undefined
    > {
        const headers = await Service.getRequestHeaders()
        const response = await axios.post(
            `${serverBaseURL}/users/check`,
            { login, password },
            { headers }
        )
        if (response.status === 200) {
            return response.data
        }
        return undefined
    }

    public static async getUsersByGroupOwner(ownerId: string): Promise<any> {
        const headers = await Service.getRequestHeaders()

        const response = await axios.get(
            `${serverBaseURL}/users/groupEmail/${ownerId}`,
            {
                headers,
            }
        )

        if (response.status === 200) {
            return response.data
        }
        return []
    }
}
export default UserApi
